import {
  fillMissingPositions,
  getPositionFromIndex
} from "@pathwright/utils/src/positioning/fractional-positioning"
import type {
  AugmentationPanelItemControl,
  AugmentationPanelItemProps,
  AugmentationPanelSectionAddProps,
  AugmentationPanelSectionListSortProps
} from "@pathwright/ux/src/components/augmentation/AugmentationPanelSection"
import { toaster } from "@pathwright/ux/src/components/chakra/ui/toaster"
import { useCallback, useMemo, useState } from "react"
import {
  CoverPageOfferFragment as CoverPageOffer,
  OfferKind,
  useDeleteOfferMutation,
  useReorderOffersMutation
} from "@pathwright/graphql-client/src/generated"
import { useCoverPageContext } from "../CoverPageProvider"
import { getCoverPageOfferPosition } from "../coverPageOfferOrdering"
import { useCoverPageTranslate } from "../utils"
import { CoverPagePanelSectionBlock } from "./CoverPagePanelSection"
import { useCoverPagePanelErrorMessage } from "./utils"
import { useCoverPageAddLabel } from "./addLabels"

// Since thesese items are displayed to the Designer,
// the icon should indicate the type/kind of offer,
// not the access state.
function getOfferIcon(
  offer: CoverPageOffer
): AugmentationPanelItemProps["icon"] {
  const { kind } = offer

  return kind === OfferKind.CreateCohort || kind === OfferKind.LicenseToSpace
    ? "group-plus"
    : kind === OfferKind.External
    ? "link-2"
    : "group"
}
function useCoverPagePanelOfferItems({
  buildOfferRoute
}: {
  buildOfferRoute: (offer: CoverPageOffer) => string
}) {
  const offers = useCoverPageContext((value) => value.offers)

  return useMemo<AugmentationPanelItemProps[]>(
    () =>
      (offers || []).map((offer) => ({
        itemKey: `edit-offer-${offer.id}`,
        icon: getOfferIcon(offer),
        label: offer.title,
        panelRoute: buildOfferRoute(offer),
        data: offer
      })),
    [buildOfferRoute, offers]
  )
}

export function CoverPagePanelOffersSection() {
  const resourceId = useCoverPageContext((value) => value.resourceId)
  const offers = useCoverPageContext((value) => value.offers)
  const refetchCoverPageOffers = useCoverPageContext(
    (value) => value.refetchCoverPageOffers
  )
  const updateCoverPageOfferPositions = useCoverPageContext(
    (value) => value.updateCoverPageOfferPositions
  )
  const [deleteOffer] = useDeleteOfferMutation()
  const [reorderOffers] = useReorderOffersMutation()
  const { t } = useCoverPageTranslate()
  const addLabel = useCoverPageAddLabel("offer")
  const getErrorMessage = useCoverPagePanelErrorMessage()
  const [removingItemKeys, setRemovingItemKeys] = useState<string[]>([])

  const handleRemoveOffer = useCallback(
    async (offerId: number) => {
      const itemKey = `edit-offer-${offerId}`

      setRemovingItemKeys((current) => current.concat(itemKey))

      try {
        await deleteOffer({
          variables: {
            resourceId,
            id: offerId
          }
        })
        await refetchCoverPageOffers()
      } finally {
        setRemovingItemKeys((current) =>
          current.filter((currentItemKey) => currentItemKey !== itemKey)
        )
      }
    },
    [deleteOffer, refetchCoverPageOffers, resourceId]
  )

  const items = useCoverPagePanelOfferItems({
    buildOfferRoute: (offer) => `${offer?.id || ""}`
  }).map((item) => {
    const offerId = Number(item.itemKey.replace("edit-offer-", ""))
    const offer = offers.find((offer) => offer.id === offerId)

    if (!offer) {
      return {
        ...item,
        sortable: false,
        controls: []
      }
    }

    return {
      ...item,
      controls: [
        {
          type: "delete" as const,
          hashKey: "offers",
          loading: removingItemKeys.includes(item.itemKey),
          handler: () => handleRemoveOffer(offer.id)
        },
        {
          type: "edit" as const,
          panelRoute: `${offer.id}`
        }
      ] as AugmentationPanelItemControl[]
    }
  })

  const addProps = useMemo<AugmentationPanelSectionAddProps<any>>(
    () => ({
      itemKey: "add-offer",
      label: addLabel,
      link: {
        panelRoute: "offers/new"
      }
    }),
    [addLabel]
  )

  const handleMove = useCallback(
    async (movedOffer: CoverPageOffer, movedIndex: number) => {
      const movedPosition = getPositionFromIndex({
        list: offers
          .filter((offer) => offer !== movedOffer)
          .map((offer) => ({
            id: offer.id,
            position: getCoverPageOfferPosition(offer)
          }))
          .filter(
            (
              positionedOffer
            ): positionedOffer is { id: number; position: string | null } =>
              !!positionedOffer.id
          ),
        index: movedIndex,
        positionKey: "position"
      })
      const positionedOffers = fillMissingPositions({
        list: offers
          .map((offer) => ({
            id: offer.id,
            position:
              offer === movedOffer
                ? movedPosition
                : getCoverPageOfferPosition(offer)
          }))
          .filter(
            (
              positionedOffer
            ): positionedOffer is { id: number; position: string | null } =>
              !!positionedOffer.id
          ),
        positionKey: "position"
      })

      const order = positionedOffers.map((positionedStoreOffer) => {
        if (!positionedStoreOffer.position) {
          throw new Error(`Unable to sort offer ${positionedStoreOffer.id}.`)
        }

        return {
          id: positionedStoreOffer.id,
          position: positionedStoreOffer.position
        }
      })

      updateCoverPageOfferPositions(order)

      try {
        await reorderOffers({
          variables: {
            resourceId,
            input: {
              order
            }
          }
        })
        await refetchCoverPageOffers()
      } catch (error) {
        updateCoverPageOfferPositions(null)
        await refetchCoverPageOffers()
        toaster.create({
          title: getErrorMessage(
            error,
            t("panel_errors.reorder_offers", {
              defaultValue: "Error reordering Cover Page's offers:"
            })
          ),
          type: "error",
          duration: 3000,
          closable: true
        })
      }
    },
    [
      offers,
      getErrorMessage,
      refetchCoverPageOffers,
      reorderOffers,
      resourceId,
      t,
      updateCoverPageOfferPositions
    ]
  )

  const sortProps = useMemo<AugmentationPanelSectionListSortProps>(
    () => ({
      onSort: ({ item, sortableItems }) => {
        const offerByItemKey = new Map<string, CoverPageOffer>(
          offers.flatMap((offer) => {
            if (!offer) return []

            return [[`edit-offer-${offer.id}`, offer]]
          })
        )
        const sortableOffers = sortableItems.map((sortableItem) => {
          const offer = offerByItemKey.get(sortableItem.itemKey)

          if (!offer) {
            throw new Error(
              `Unable to sort missing offer ${sortableItem.itemKey}.`
            )
          }

          return offer
        })
        const movedOffer = offerByItemKey.get(item.itemKey)
        const movedIndex = movedOffer
          ? sortableOffers.findIndex((offer) => offer === movedOffer)
          : -1

        if (!movedOffer || movedIndex < 0) {
          throw new Error(`Unable to sort missing offer ${item.itemKey}.`)
        }

        handleMove(movedOffer, movedIndex)
      },
      onMove: (item, index) => handleMove(item.data, index)
    }),
    [handleMove]
  )

  return (
    <CoverPagePanelSectionBlock
      heading="Offers"
      sectionKey="offers"
      items={items}
      addProps={addProps}
      sortProps={sortProps}
    />
  )
}
